<template>
  <div>
    <section class="pt-16">
      <div class="mb-12 text-center">
        <div
          class="text-lg font-light leading-7"
          :class="[view === 'step1' ? 'mb-6' : 'mb-8']"
        >
          <div v-if="view === 'step1'">
            <div>ユーザー登録を行います。</div>
            <div>約3分で完了します。</div>
          </div>
          <div v-if="view === 'step2'">ユーザー情報をご入力ください。</div>
          <div v-if="view === 'step3'">
            企業を探す上で重視することを教えてください。
          </div>
          <div v-if="view === 'step4'">
            登録するサービスのカテゴリーを選択してください。
          </div>
        </div>

        <div v-if="view === 'completed' && !isInvite" class="text-center">
          <div class="text-lg mb-8">アカウント申請が完了しました</div>
          <div class="leading-6">
            ただいま会員審査を行っています。審査完了後、メールが届きますのでログインを行ってください。
          </div>
          <div class="leading-6 mb-16">
            2日経っても連絡がない場合は、お問い合わせフォームよりご連絡ください。
          </div>
        </div>

        <div
          v-if="view === 'completed' && isInvite"
          class="text-center text-gray-92"
        >
          <div class="title-lg leading-normal">仮登録が完了しました。</div>
          <div class="body-md leading-normal mt-4">
            登録いただいたメールアドレスに確認のメールをお送りしました。<br />
            メールに記載のURLからログインしてください。
          </div>
        </div>

        <div v-if="view === 'step1'" class="mb-8">
          <span class="text-gray-62">カスタマーとしてのみ登録する場合は</span>
          <a
            href="https://buyer.imitsu-cloud.jp/registration"
            class="inline-block text-gray-62 border-b"
            >こちら</a
          >
        </div>

        <div
          v-if="view !== 'completed'"
          class="flex justify-center mx-auto font-semibold"
          style="max-width: 600px"
        >
          <BaseStep
            v-for="step in steps"
            :key="step.view"
            :name="step.name"
            :is-active="view === step.view"
          />
        </div>
      </div>

      <div v-if="showForm">
        <RegistrationStep1
          v-show="view === 'step1'"
          :formData="formData"
          :initialValues="{ companyName: inputValues.companyName || '' }"
          @change-view="changeView"
        />
        <RegistrationStep2
          v-show="view === 'step2'"
          :formData="formData"
          :initialValues="{ email: inputValues.email || '' }"
          :hasPrevious="!isInvite"
          @change-view="changeView"
        />
        <RegistrationStep3
          v-show="view === 'step3'"
          :formData="formData"
          :hasNext="!isInvite"
          @change-view="changeView"
        />
        <RegistrationStep4
          v-show="view === 'step4'"
          @change-view="changeView"
        />
      </div>
    </section>
  </div>
</template>

<script>
import {
  reactive,
  onMounted,
  inject,
  onUnmounted,
  toRefs,
  computed,
} from 'vue';
import { useRouter } from 'vue-router';

import axios from '@/utils/axios.js';
import { addHistoryAlert, removeHistoryAlert } from '@/utils/historyAlert.js';
import BaseStep from '@/components/BaseStep.vue';
import RegistrationStep1 from '@/views/registration/RegistrationStep1.vue';
import RegistrationStep2 from '@/views/registration/RegistrationStep2.vue';
import RegistrationStep3 from '@/views/registration/RegistrationStep3.vue';
import RegistrationStep4 from '@/views/registration/RegistrationStep4.vue';

export default {
  components: {
    BaseStep,
    RegistrationStep1,
    RegistrationStep2,
    RegistrationStep3,
    RegistrationStep4,
  },
  setup() {
    const router = useRouter();
    const query = useRouter().currentRoute.value.query;
    const isInvite = Boolean(query.invite_id) && Boolean(query.signature);
    const globalMethods = inject('globalMethods');

    const steps = computed(() => {
      if (isInvite) {
        // Step1,Step4の情報は招待元から引継ぐため入力不要
        return [
          { view: 'step2', name: '基本情報' },
          { view: 'step3', name: 'ニーズ情報' },
        ];
      }
      return [
        { view: 'step1', name: '企業情報' },
        { view: 'step2', name: '基本情報' },
        { view: 'step3', name: 'ニーズ情報' },
        { view: 'step4', name: 'サービス選択' },
      ];
    });

    const state = reactive({
      view: steps.value[0].view,
      showForm: false,
      formData: {},
      inputValues: {},
    });

    onMounted(async () => {
      if (
        router.currentRoute.value.name === 'RegistrationCompleted' &&
        state.view !== 'completed'
      ) {
        return router.push('/404');
      }

      addHistoryAlert();

      await Promise.all([
        axios.get('/seller-items'),
        axios.post('/sellers/invite-check'),
      ])
        .then(async ([sellerItems, invite]) => {
          state.formData = sellerItems.data.data;

          if (invite.data.result) {
            const { email, company_name } = invite.data.data;
            state.inputValues = { email, companyName: company_name };
          }

          if (!invite.data.result && isInvite) {
            throw new Error(invite.data.message);
          }
        })
        .catch((e) => {
          globalMethods.updateModal(
            true,
            'URLが正しくないか、通信エラーが発生しました。',
            e.message || e.systemMessage || '',
            () => {
              removeHistoryAlert();
              router.push({ name: 'Login' });
            }
          );
          globalMethods.sendLog(e);
        })
        .finally(() => {
          state.showForm = true;
        });
    });

    onUnmounted(() => {
      removeHistoryAlert();
    });

    const changeView = async ({ view, inputValues = {} }) => {
      state.inputValues = { ...state.inputValues, ...inputValues };

      if (view !== 'completed') {
        state.view = view;
        window.scrollTo(0, 0);
        return;
      }

      if (isInvite) {
        await registerInvitedUser();
      } else {
        await registerUser();
      }

      state.view = view;
      window.scrollTo(0, 0);
      router.push({ path: '/registration/completed/' });
    };

    const registerUser = async () => {
      const items = Object.entries(state.inputValues).reduce(
        (prev, [key, value]) => {
          if (key.indexOf('itemId') === -1 || !value) return prev;
          const id = +key.replace('itemId', '');
          return [...prev, { id, values: value }];
        },
        []
      );

      const postData = {
        name: state.inputValues.name,
        email: state.inputValues.email,
        password: state.inputValues.password,
        tel: state.inputValues.tel,
        company_name: state.inputValues.companyName,
        request_category_ids: state.inputValues.request_category_ids,
        items,
      };

      try {
        await axios.post('/sellers/register', postData);
      } catch (e) {
        const message =
          e.response.status === 409
            ? '既に登録されているメールアドレスです。'
            : e.message;

        globalMethods.updateModal(true, '登録に失敗しました。', message);
      }
    };

    const registerInvitedUser = async () => {
      const items = Object.entries(state.inputValues).reduce(
        (prev, [key, value]) => {
          if (key.indexOf('itemId') === -1 || !value) return prev;
          const id = +key.replace('itemId', '');
          return [...prev, { id, values: value }];
        },
        []
      );

      const postData = {
        name: state.inputValues.name,
        email: state.inputValues.email,
        password: state.inputValues.password,
        tel: state.inputValues.tel,
        items,
      };

      try {
        await axios.post('/sellers/invite-user-register', postData);
      } catch (e) {
        const message =
          e.response.status === 409
            ? '既に登録されているメールアドレスです。'
            : e.message;

        globalMethods.updateModal(true, '登録に失敗しました。', message);
      }
    };

    return {
      ...toRefs(state),
      steps,
      changeView,
      isInvite,
    };
  },
};
</script>

<style lang="scss" scoped></style>
